import { Package, Plus, Search, SlidersHorizontal } from 'lucide-react'
import { useQueryState } from 'nuqs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import useSWR from 'swr'

import { EmptyState } from '@/components/EmptyState'
import { PageHeader, SegmentedTabs } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Products } from '@/containers/Products/Products'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { useFeatureCheck } from '@/hooks/useFeatureCheck'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { Category } from '@/interfaces/catalog/Category'
import { Product } from '@/interfaces/catalog/Product'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { buildProductSummaries, CatalogSku, formatCount } from '@/lib/catalog/catalogTypes'
import { flattenCategories } from '@/lib/catalog/categoryUtils'
import { Role } from '@/types/User'

type StatusFilter = 'all' | 'active' | 'inactive'
const PAGE = 25

const collectCategories = (
  categories: Category[],
  parentPath = '',
  map = new Map<string, { name: string; path: string }>(),
) => {
  for (const category of categories) {
    const path = parentPath ? `${parentPath} > ${category.name}` : category.name
    map.set(category.id, { name: category.name, path })
    if (category.children?.length) collectCategories(category.children, path, map)
  }
  return map
}

export default function CatalogProductsPage() {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog' })
  const [mainCategoryId, setMainCategoryId] = useQueryState('mainCategoryId')
  const [statusParam, setStatusParam] = useQueryState('status')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const canCreate = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const { isEnabled: warehouseEnabled } = useFeatureCheck('warehouse')

  const status = (statusParam as StatusFilter) || 'all'

  const {
    data: productsResponse,
    isLoading,
    mutate,
  } = useSWR<{ data?: Product[]; total?: number }>(
    mainCategoryId ? `/api/v2/products?mainCategoryId=${mainCategoryId}` : '/api/v2/products',
    apiFetcher,
  )
  const { data: categoriesResponse } = useSWR<{ data?: Category[]; total?: number }>('/api/v2/categories', apiFetcher)
  const { data: skusResponse, mutate: mutateSkus } = useSWR<{ data?: CatalogSku[]; total?: number }>(
    '/api/v2/skus',
    apiFetcher,
  )
  const { data: stockResponse } = useSWR<{ data?: StockItem[]; total?: number }>(
    warehouseEnabled ? '/api/v2/stock-items' : null,
    apiFetcher,
  )

  const products = useMemo(() => productsResponse?.data ?? [], [productsResponse])
  const categories = useMemo(() => categoriesResponse?.data ?? [], [categoriesResponse])
  const categoryOptions = useMemo(() => flattenCategories(categories), [categories])
  const categoryById = useMemo(() => collectCategories(categories), [categories])
  const summaries = useMemo(
    () => buildProductSummaries(skusResponse?.data ?? [], warehouseEnabled ? (stockResponse?.data ?? []) : null),
    [skusResponse, stockResponse, warehouseEnabled],
  )

  const counts = useMemo(() => {
    const active = products.filter((p) => p.isActive !== false).length
    return { all: products.length, active, inactive: products.length - active }
  }, [products])

  const skuCodesByProduct = useMemo(() => {
    const map = new Map<string, string>()
    for (const sku of skusResponse?.data ?? []) {
      map.set(sku.productId, `${map.get(sku.productId) ?? ''} ${sku.code} ${sku.name}`)
    }
    return map
  }, [skusResponse])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return products
      .filter((p) => status === 'all' || (status === 'active' ? p.isActive !== false : p.isActive === false))
      .filter((p) => {
        if (!q) return true
        return [p.name, p.externalId, skuCodesByProduct.get(p.id)].some((v) => v?.toLowerCase().includes(q))
      })
  }, [products, status, query, skuCodesByProduct])

  const pageItems = filtered.slice(page * PAGE, (page + 1) * PAGE)
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE))

  useSetPageHeader({
    breadcrumbs: [
      { href: '/catalog', children: t('title') },
      { href: '/catalog/products', children: t('products.title') },
    ],
  })

  const setFilter = (value: StatusFilter) => {
    setStatusParam(value === 'all' ? null : value)
    setPage(0)
  }

  const filteredOut = products.length > 0 || !!mainCategoryId

  return (
    <>
      <title>{t('products.title')}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={t('products.title')}
          description={isLoading ? undefined : formatCount(products.length, 'produkt', 'produkty', 'produktů')}
          actions={
            <>
              <Select
                value={mainCategoryId || 'all'}
                onValueChange={(value) => {
                  setMainCategoryId(value === 'all' ? null : value)
                  setPage(0)
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue placeholder={t('products.filter.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.filter.allCategories')}</SelectItem>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" asChild>
                <Link to="/catalog/properties">
                  <SlidersHorizontal />
                  {t('properties.title')}
                </Link>
              </Button>
              <Button asChild>
                <Link to="/catalog/product/new">
                  <Plus />
                  {t('products.actions.create')}
                </Link>
              </Button>
            </>
          }
        />

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <SegmentedTabs<StatusFilter>
            value={status}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Vše', count: counts.all },
              { value: 'active', label: 'Aktivní', count: counts.active },
              { value: 'inactive', label: 'Neaktivní', count: counts.inactive },
            ]}
          />
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setPage(0)
              }}
              placeholder="Název nebo kód SKU"
              className="pl-8"
            />
          </div>
        </div>

        <Products
          products={pageItems}
          summaries={summaries}
          categoryById={categoryById}
          isLoading={isLoading}
          showStock={!!warehouseEnabled}
          refresh={() => {
            mutate()
            mutateSkus()
          }}
          empty={
            <div className="p-6">
              <EmptyState icon={Package}
                title={filteredOut ? 'Filtru neodpovídá žádný produkt' : t('products.empty')}
                description={filteredOut ? 'Zkuste jiný stav, kategorii nebo hledaný výraz.' : undefined}
                ctaButton={
                  !filteredOut && canCreate ? (
                    <Button asChild>
                      <Link to="/catalog/product/new">{t('products.list.emptyState.cta')}</Link>
                    </Button>
                  ) : undefined
                }
              />
            </div>
          }
          footer={
            filtered.length > PAGE && (
              <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
                <span className="tabular">
                  {page * PAGE + 1}-{Math.min((page + 1) * PAGE, filtered.length)} z {filtered.length}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Předchozí
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
                    Další
                  </Button>
                </div>
              </div>
            )
          }
        />
      </div>
    </>
  )
}
